import { useState } from "react";
import {
  createOpcaoCircuito,
  updateOpcaoCircuito,
  deleteOpcaoCircuito,
} from "../opcaoCircuitoService";

// Mapeia o grupo (roteamento/entregaFinal/entregaFinalCliente) para o "type" do
// catálogo global de opcaoCircuito — mesmo valor de equipRouter/equipDelivery/
// equipLastMile que o upsert do circuito espera de volta em formData
const EQUIP_TYPE_BY_TIPO = {
  roteamento: "equipRouter",
  entregaFinal: "equipDelivery",
  entregaFinalCliente: "equipLastMile",
};

// Hook parametrizado que reúne o CRUD inline + menu de ações de um grupo de
// opções globais de equipamento (roteamento, entrega final ou entrega final
// cliente). Extraído de circuito/index.js (Task 3 da decomposição): eram três
// cópias quase idênticas do mesmo estado/CRUD, uma por grupo — aqui uma única
// instância parametrizada por `tipo` cobre qualquer um dos três.
export default function useOpcoesEquipamento({
  tipo,
  opcoes,
  setOpcoes,
  valorSelecionado,
  onSelecionar,
  setSnackbar,
  // setLoading do circuito aberto (dono é circuito/index.js desde a Task 7) — as operações abaixo já chamavam essa função antes, só relocada
  setLoading = () => {},
}) {
  const equipType = EQUIP_TYPE_BY_TIPO[tipo];

  // Loading próprio desta instância. No original, os três grupos mutavam direto
  // um objeto loadingEquipment compartilhado (loadingEquipment[tipo] = true),
  // sem setState — essa mutação não disparava re-render por si só, o loading só
  // "aparecia" porque outro setState no mesmo fluxo (setLoading) já causava o
  // re-render seguinte, que então lia o objeto já mutado. Grep em todo
  // circuito/index.js confirmou que loadingEquipment não é lido por nenhum
  // bloco fora dos três grupos de equipamento, então cada instância deste hook
  // pode ter seu próprio estado de loading de verdade sem mudar o resultado
  // visível (loading aparece/desaparece nos mesmos momentos de antes).
  const [loadingOpcao, setLoadingOpcao] = useState(false);

  // Controle do dropdown do Autocomplete
  const [aberto, setAberto] = useState(false);

  // Opção "em foco" pelo menu de ações (3 pontinhos) — reaproveitada tanto pela
  // edição quanto pela exclusão, igual ao original (um único selectOptionX)
  const [opcaoEmAcao, setOpcaoEmAcao] = useState({});
  const [anchorMenuAcao, setAnchorMenuAcao] = useState(null);
  const [edicaoAberta, setEdicaoAberta] = useState(false);
  const [exclusaoAberta, setExclusaoAberta] = useState(false);

  // Abre o menu de ações da opção clicada; o Autocomplete continua aberto por
  // trás dele (open = aberto || anchorMenuAcao, ver Autocomplete no index.js)
  const abrirMenuAcao = (opcao, event) => {
    setAberto(true);
    setOpcaoEmAcao(opcao);
    setAnchorMenuAcao(event?.currentTarget);
  };

  const fecharMenuAcao = () => {
    setAnchorMenuAcao(null);
  };

  const abrirEdicao = () => {
    setEdicaoAberta(true);
  };

  const abrirExclusao = () => {
    setExclusaoAberta(true);
  };

  const alterarLabelEmEdicao = (novoLabel) => {
    setOpcaoEmAcao((prev) => ({ ...prev, label: novoLabel }));
  };

  // Exclui a opção global — afeta todos os clientes que a usam (aviso fica no
  // texto do modal de confirmação, não muda aqui)
  async function excluirOpcao() {
    try {
      setLoadingOpcao(true);
      setLoading(true);
      await deleteOpcaoCircuito(opcaoEmAcao?.id);
      const newOptions = opcoes?.filter(
        (option) => option?.id !== opcaoEmAcao?.id,
      );
      setOpcoes(newOptions);
      if (valorSelecionado?.id === opcaoEmAcao?.id) {
        onSelecionar(null);
      }
      setSnackbar({
        children: "Item excluído com sucesso",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Erro ao excluir item",
        severity: "error",
      });
    } finally {
      setExclusaoAberta(false);
      setOpcaoEmAcao(null);
      setAnchorMenuAcao(null);
      setLoading(false);
      setLoadingOpcao(false);
    }
  }

  // Renomeia a opção global; título precisa ser único dentro do grupo
  async function renomearOpcao() {
    try {
      setLoadingOpcao(true);
      setLoading(true);
      if (
        !opcaoEmAcao?.label?.trim() ||
        opcoes?.some(
          (type) =>
            type?.label?.toLowerCase() ===
            opcaoEmAcao?.label?.trim()?.toLowerCase(),
        )
      ) {
        throw new Error("Titulo precisa ser único");
      }
      await updateOpcaoCircuito({
        id: opcaoEmAcao?.id,
        label: opcaoEmAcao?.label,
      });
      const newOptions = opcoes?.map((option) => {
        if (option?.id === opcaoEmAcao?.id) {
          return { ...option, label: opcaoEmAcao?.label };
        } else {
          return option;
        }
      });
      setOpcoes(newOptions);
      if (valorSelecionado?.id === opcaoEmAcao?.id) {
        onSelecionar({ ...valorSelecionado, label: opcaoEmAcao?.label });
      }
      setSnackbar({
        children: "Item editado com sucesso",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      if (error?.message === "Titulo precisa ser único") {
        setSnackbar({
          children: "Titulo precisa ser único",
          severity: "error",
        });
      } else {
        setSnackbar({
          children: "Erro ao editar item",
          severity: "error",
        });
      }
    } finally {
      setOpcaoEmAcao(null);
      setAnchorMenuAcao(null);
      setEdicaoAberta(false);
      setLoading(false);
      setLoadingOpcao(false);
    }
  }

  // Cria a opção a partir do texto digitado no Autocomplete e já seleciona o
  // item recém-criado (mesma ordem do bloco original, index.js:1109 antes desta
  // extração)
  async function criarOpcao(newLabel) {
    try {
      setLoadingOpcao(true);
      setLoading(true);
      const criado = await createOpcaoCircuito({
        label: newLabel,
        type: equipType,
      });
      const novoItem = { ...criado, id: criado?.optionId };

      setOpcoes((prev) => [...(prev || []), novoItem]);
      onSelecionar(novoItem);
      setAberto(false);
      setSnackbar({
        children: "Opção criada com sucesso",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Falha ao criar opção",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setLoadingOpcao(false);
    }
  }

  return {
    aberto,
    setAberto,
    loadingOpcao,
    anchorMenuAcao,
    abrirMenuAcao,
    fecharMenuAcao,
    opcaoEmAcao,
    edicaoAberta,
    abrirEdicao,
    setEdicaoAberta,
    exclusaoAberta,
    abrirExclusao,
    setExclusaoAberta,
    alterarLabelEmEdicao,
    criarOpcao,
    renomearOpcao,
    excluirOpcao,
  };
}
